import { useQuery } from '@apollo/client/react'
import { Loader } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { GQL_PING } from '~/gql/common/common'
import { env } from '~/helpers/env'
import { useAuth } from '~/helpers/store/auth'
import Login from '~/pages/authentication/Login'

function App() {
  const { isLoading: authLoading, payload } = useAuth()
  const [apiCheck, setApiCheck] = useState<boolean>(false)
  const [loadingApi, setLoadingApi] = useState(true)
  const { data, loading } = useQuery(GQL_PING)
  const { t } = useTranslation()

  useEffect(() => {
    const fetchApi = async () => {
      try {
        const response = await fetch(`${env.api.url}/`)
        const resp = await response.json()
        setLoadingApi(false)
        if (resp.status === 'ok') {
          return true
        }
      } catch (_e) {
        setLoadingApi(false)
        return false
      }
    }
    const apiVerify = async (): Promise<void> => {
      const result = await fetchApi()
      result ? setApiCheck(true) : setApiCheck(false)
    }
    apiVerify()
  }, [])

  return (
    <div className="relative w-full min-h-dvh flex flex-col justify-center items-center">
      {env.app.env !== 'live' && (
        <div className="m-0 max-w-full w-full sm:fixed sm:top-0 overflow-hidden p-2 bg-background text-foreground border-b border-border text-sm md:block">
          <div className="container mx-auto flex items-center justify-center flex-wrap gap-4">
            <div className="flex items-center gap-2">
              <span className="font-medium">
                {t('common.environment')}
                :
              </span>
              <span className="px-2 py-0.5 rounded-full bg-primary/10 text-primary font-medium">
                {env.app.env}
              </span>
            </div>

            <div className="flex items-center gap-2">
              <span className="font-medium">Api:</span>
              {loadingApi
                ? (
                    <Loader className="w-4 h-4 animate-spin text-muted-foreground" />
                  )
                : (
                    <span className={`px-2 py-0.5 rounded-full font-medium ${
                      apiCheck
                        ? 'bg-green-500/10 text-green-500 dark:bg-green-500/20'
                        : 'bg-red-500/10 text-red-500 dark:bg-red-500/20'
                    }`}
                    >
                      {apiCheck ? t('is.reachable') : t('is.not.reachable')}
                    </span>
                  )}
            </div>

            <div className="flex items-center gap-2">
              <span className="font-medium">Gql:</span>
              {loading
                ? (
                    <Loader className="w-4 h-4 animate-spin text-muted-foreground" />
                  )
                : (
                    <span className={`px-2 py-0.5 rounded-full font-medium ${
                      data?.getHealthGql.time
                        ? 'bg-green-500/10 text-green-500 dark:bg-green-500/20'
                        : 'bg-red-500/10 text-red-500 dark:bg-red-500/20'
                    }`}
                    >
                      {data?.getHealthGql.time ? t('is.reachable') : t('is.not.reachable')}
                    </span>
                  )}
            </div>

            <div className="flex items-center gap-2">
              <span className="font-medium">Version:</span>
              {loading
                ? (
                    <Loader className="w-4 h-4 animate-spin text-muted-foreground" />
                  )
                : (() => {
                    const apiVersion = data?.getHealthGql.version
                    const match = apiVersion === env.app.version
                    return match
                      ? (
                          <span className="px-2 py-0.5 rounded-full bg-green-500/10 text-green-500 dark:bg-green-500/20 font-medium">
                            {env.app.version}
                          </span>
                        )
                      : (
                          <span className="px-2 py-0.5 rounded-full bg-red-500/10 text-red-500 dark:bg-red-500/20 font-medium">
                            {`web: ${env.app.version} | api: ${apiVersion ?? '—'}`}
                          </span>
                        )
                  })()}
            </div>
          </div>
        </div>
      )}
      {authLoading
        ? (
            <div className="flex flex-col items-center gap-4">
              <Loader className="w-8 h-8 animate-spin" />
              <p>
                {t('common.loading')}
                ...
              </p>
            </div>
          )
        : !payload
            ? (
                <div className="w-full p-5 flex justify-center items-center">
                  <Login />
                </div>
              )
            : null}
    </div>
  )
}

export default App
